import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TreeTableModule } from 'primeng/treetable';
import { TagModule } from 'primeng/tag';

export interface JsonToken {
  type: 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punctuation' | 'whitespace';
  text: string;
}

interface JsonDisplayLine {
  kind: 'same' | 'add' | 'del';
  tokens: JsonToken[];
}

interface JsonTreeRow {
  name: string;
  type: 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';
  kind: 'same' | 'add' | 'del' | 'upd';
  oldValue: string;
  newValue: string;
}

@Component({
  selector: 'app-json-viewer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, SelectButtonModule, TreeTableModule, TagModule],
  template: `
    <div class="flex flex-col gap-2">
      <div class="flex flex-col items-end gap-2">
        <p-selectButton
          [options]="viewOptions"
          [ngModel]="viewMode()"
          (ngModelChange)="viewMode.set($event)"
          optionLabel="label"
          optionValue="value"
          [allowEmpty]="false"
          size="small"
        >
          <ng-template pTemplate="item" let-item>
            <span class="flex items-center gap-1.5 px-1">
              <i [class]="item.icon" class="text-xs"></i>
              <span>{{ item.label }}</span>
            </span>
          </ng-template>
        </p-selectButton>
        @if (viewMode() === 'tree') {
          <p-selectButton
            [options]="treeFilterOptions"
            [ngModel]="treeFilter()"
            (ngModelChange)="treeFilter.set($event)"
            optionLabel="label"
            optionValue="value"
            [allowEmpty]="false"
            size="small"
          >
            <ng-template pTemplate="item" let-item>
              <span class="flex items-center gap-1.5 px-1">
                <i [class]="item.icon" class="text-xs"></i>
                <span>{{ item.label }}</span>
              </span>
            </ng-template>
          </p-selectButton>
        }
      </div>

      @if (viewMode() === 'json') {
        <div class="json-viewer overflow-auto rounded-lg border border-[#3c3c3c] font-mono text-[13px] leading-[18px]">
          @for (line of highlightedLines(); track $index) {
            <div class="json-line flex min-w-max" [class.line-add]="line.kind === 'add'" [class.line-del]="line.kind === 'del'">
              <span class="json-gutter w-12 shrink-0 select-none pr-3 text-right">{{ $index + 1 }}</span>
              <span class="json-mark w-4 shrink-0 select-none">{{ lineMark(line.kind) }}</span>
              <span class="json-code pr-4 whitespace-pre">
                @for (token of line.tokens; track $index) {
                  <span [class]="tokenClass(token.type)">{{ token.text }}</span>
                }
              </span>
            </div>
          }
        </div>
      } @else {
        <p-treetable
          [value]="treeNodes()"
          styleClass="audit-tree-table p-datatable-sm"
          [tableStyle]="{ 'min-width': '40rem' }">
          <ng-template #header>
            <tr>
              <th>Propiedad</th>
              <th>Anterior</th>
              <th>Nuevo</th>
            </tr>
          </ng-template>
          <ng-template #body let-rowNode let-rowData="rowData">
            <tr [ttRow]="rowNode" [ngClass]="'kind-' + rowData.kind">
              <td>
                <div class="flex items-center gap-2 min-w-0">
                  <p-treetable-toggler [rowNode]="rowNode" />
                  <span class="font-mono text-sm truncate">{{ rowData.name }}</span>
                  @if (rowData.kind !== 'same') {
                    <p-tag
                      [value]="kindLabel(rowData.kind)"
                      [severity]="kindSeverity(rowData.kind)"
                      size="small"
                      styleClass="!text-[10px] !leading-none !py-0.5 !px-1.5 !font-medium"
                    />
                  }
                </div>
              </td>
              <td [class.col-old]="rowData.kind === 'del' || rowData.kind === 'upd'">
                <span class="font-mono text-sm break-all" [class.line-through]="rowData.kind === 'del' || rowData.kind === 'upd'">
                  {{ rowData.oldValue }}
                </span>
              </td>
              <td [class.col-new]="rowData.kind === 'add' || rowData.kind === 'upd'">
                <span class="font-mono text-sm break-all">{{ rowData.newValue }}</span>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="3" class="text-center text-slate-500 py-6">No hay datos para mostrar.</td>
            </tr>
          </ng-template>
        </p-treetable>
      }
    </div>
  `,
  styles: [`
    .json-viewer {
      background: #1e1e1e;
      color: #d4d4d4;
      min-height: 12rem;
    }
    .json-line:hover {
      background: #2a2d2e;
    }
    .json-line.line-add {
      background: rgba(46, 160, 67, 0.18);
    }
    .json-line.line-del {
      background: rgba(248, 81, 73, 0.18);
    }
    :host ::ng-deep .audit-tree-table .col-old {
      background: rgb(254 226 226);
      color: rgb(153 27 27);
    }
    :host ::ng-deep .audit-tree-table .col-new {
      background: rgb(220 252 231);
      color: rgb(22 101 52);
    }
    :host ::ng-deep .audit-tree-table tr.kind-add {
      background: rgb(240 253 244);
    }
    :host ::ng-deep .audit-tree-table tr.kind-del {
      background: rgb(254 242 242);
    }
    :host ::ng-deep .audit-tree-table tr.kind-upd {
      background: rgb(255 251 235);
    }
    .json-gutter {
      color: #858585;
      background: #1e1e1e;
      border-right: 1px solid #3c3c3c;
    }
    .json-mark {
      color: #6e7681;
      text-align: center;
    }
    .line-add .json-mark { color: #3fb950; }
    .line-del .json-mark { color: #f85149; }
    .tok-key { color: #9cdcfe; }
    .tok-string { color: #ce9178; }
    .tok-number { color: #b5cea8; }
    .tok-boolean, .tok-null { color: #569cd6; }
    .tok-punctuation { color: #d4d4d4; }
  `]
})
export class JsonViewerComponent {
  data = input<unknown>(null);
  previous = input<unknown>(null);

  readonly viewMode = signal<'json' | 'tree'>('tree');
  readonly treeFilter = signal<'all' | 'changes'>('all');
  readonly viewOptions = [
    { label: 'Árbol', value: 'tree', icon: 'pi pi-sitemap' },
    { label: 'JSON', value: 'json', icon: 'pi pi-code' }
  ];
  readonly treeFilterOptions = [
    { label: 'Toda la tabla', value: 'all', icon: 'pi pi-list' },
    { label: 'Solo cambios', value: 'changes', icon: 'pi pi-filter' }
  ];

  readonly highlightedLines = computed(() => this.buildLines());
  readonly treeNodes = computed(() => {
    const nodes = this.buildTreeNodes('', this.sortValue(this.data()), this.sortValue(this.previous()));
    return this.treeFilter() === 'changes' ? this.filterChangedNodes(nodes) : nodes;
  });

  tokenClass(type: JsonToken['type']): string {
    return `tok-${type}`;
  }

  lineMark(kind: JsonDisplayLine['kind']): string {
    if (kind === 'add') return '+';
    if (kind === 'del') return '-';
    return ' ';
  }

  kindLabel(kind: JsonTreeRow['kind']): string {
    if (kind === 'add') return 'Nuevo';
    if (kind === 'del') return 'Eliminado';
    if (kind === 'upd') return 'Cambio';
    return '';
  }

  kindSeverity(kind: JsonTreeRow['kind']): 'success' | 'danger' | 'warn' | 'secondary' {
    if (kind === 'add') return 'success';
    if (kind === 'del') return 'danger';
    if (kind === 'upd') return 'warn';
    return 'secondary';
  }

  private filterChangedNodes(nodes: TreeNode<JsonTreeRow>[]): TreeNode<JsonTreeRow>[] {
    const filtered: TreeNode<JsonTreeRow>[] = [];
    for (const node of nodes) {
      const children = node.children?.length ? this.filterChangedNodes(node.children) : [];
      const changed = node.data?.kind !== undefined && node.data.kind !== 'same';
      if (changed || children.length > 0) {
        filtered.push({
          ...node,
          children,
          expanded: true,
          leaf: children.length === 0
        });
      }
    }
    return filtered;
  }

  private buildTreeNodes(parentPath: string, current: unknown, previous: unknown): TreeNode<JsonTreeRow>[] {
    if (this.isPlainObject(current) || this.isPlainObject(previous)) {
      const currentObj = this.isPlainObject(current) ? current : {};
      const previousObj = this.isPlainObject(previous) ? previous : {};
      const keys = [...new Set([...Object.keys(previousObj), ...Object.keys(currentObj)])].sort((a, b) => {
        const rankDiff = this.typeRank(currentObj[a] ?? previousObj[a]) - this.typeRank(currentObj[b] ?? previousObj[b]);
        return rankDiff !== 0 ? rankDiff : a.localeCompare(b);
      });
      return keys.map((key) => this.toTreeNode(key, this.joinPath(parentPath, key), currentObj[key], previousObj[key]));
    }
    if (Array.isArray(current) || Array.isArray(previous)) {
      return this.buildArrayNodes(parentPath, Array.isArray(current) ? current : [], Array.isArray(previous) ? previous : []);
    }
    return [];
  }

  private toTreeNode(key: string, path: string, current: unknown, previous: unknown): TreeNode<JsonTreeRow> {
    const presentCurrent = current !== undefined;
    const presentPrevious = previous !== undefined;
    const type = this.valueType(presentCurrent ? current : previous);
    const expandable = type === 'object' || type === 'array';
    let kind: JsonTreeRow['kind'] = 'same';
    if (presentCurrent && !presentPrevious) {
      kind = 'add';
    } else if (!presentCurrent && presentPrevious) {
      kind = 'del';
    } else if (!expandable && !this.sameValue(current, previous)) {
      kind = 'upd';
    }
    const children = expandable
      ? this.buildTreeNodes(path, presentCurrent ? current : undefined, presentPrevious ? previous : undefined)
      : undefined;
    return {
      key: path,
      expanded: true,
      leaf: !expandable,
      children,
      data: {
        name: this.nodeName(key, presentCurrent ? current : previous),
        type,
        kind,
        oldValue: this.cellValue(previous, expandable, kind === 'add'),
        newValue: this.cellValue(current, expandable, kind === 'del')
      }
    };
  }

  private cellValue(value: unknown, expandable: boolean, hide: boolean): string {
    if (hide || value === undefined) {
      return '—';
    }
    if (expandable) {
      return '';
    }
    return this.previewValue(value);
  }

  private buildArrayNodes(parentPath: string, currentArr: unknown[], previousArr: unknown[]): TreeNode<JsonTreeRow>[] {
    if (this.isIdentifiedList(currentArr) || this.isIdentifiedList(previousArr)) {
      const currentById = this.indexById(currentArr);
      const previousById = this.indexById(previousArr);
      const ids = [...new Set([...previousById.keys(), ...currentById.keys()])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
      return ids.map((id) =>
        this.toTreeNode(`#${id}`, this.joinPath(parentPath, id), currentById.get(id), previousById.get(id))
      );
    }
    const length = Math.max(currentArr.length, previousArr.length);
    const nodes: TreeNode<JsonTreeRow>[] = [];
    for (let i = 0; i < length; i++) {
      nodes.push(this.toTreeNode(String(i), this.joinPath(parentPath, String(i)), currentArr[i], previousArr[i]));
    }
    return nodes;
  }

  private nodeName(key: string, value: unknown): string {
    if (key.startsWith('#') || /^\d+$/.test(key)) {
      return this.arrayItemLabel(key, value);
    }
    return key;
  }

  private arrayItemLabel(key: string, value: unknown): string {
    const id = this.itemId(value);
    const idPart = id ? `#${id}` : (/^\d+$/.test(key) ? `[${key}]` : key);
    const label = this.humanLabel(value);
    return label ? `${label} (${idPart})` : idPart;
  }

  private humanLabel(value: unknown): string | null {
    if (!this.isPlainObject(value)) {
      return null;
    }
    for (const field of ['name', 'description', 'code', 'number', 'taxKey', 'displayName', 'legalName', 'comercialName']) {
      const raw = value[field];
      if (raw != null && String(raw).trim() !== '') {
        return String(raw);
      }
    }
    return null;
  }

  private isIdentifiedList(items: unknown[]): boolean {
    return items.length > 0 && items.every((item) => this.itemId(item) !== null);
  }

  private indexById(items: unknown[]): Map<string, unknown> {
    const indexed = new Map<string, unknown>();
    for (const item of items) {
      const id = this.itemId(item);
      if (id) {
        indexed.set(id, item);
      }
    }
    return indexed;
  }

  private itemId(value: unknown): string | null {
    if (!this.isPlainObject(value) || value['id'] === undefined || value['id'] === null) {
      return null;
    }
    return String(value['id']);
  }

  private joinPath(parent: string, key: string): string {
    return parent ? `${parent}.${key}` : key;
  }

  private typeRank(value: unknown): number {
    const type = this.valueType(value);
    if (type === 'object') return 1;
    if (type === 'array') return 2;
    return 0;
  }

  private valueType(value: unknown): JsonTreeRow['type'] {
    if (value === null || value === undefined) return 'null';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    return 'string';
  }

  private previewValue(value: unknown): string {
    if (value === undefined) return 'undefined';
    try {
      const raw = JSON.stringify(value);
      return raw.length > 120 ? `${raw.slice(0, 117)}…` : raw;
    } catch {
      return '"?"';
    }
  }

  private sameValue(left: unknown, right: unknown): boolean {
    try {
      return JSON.stringify(left) === JSON.stringify(right);
    } catch {
      return left === right;
    }
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }

  private buildLines(): JsonDisplayLine[] {
    const currentPretty = this.toPrettyJson(this.sortValue(this.data()));
    const previousValue = this.previous();
    if (previousValue === null || previousValue === undefined) {
      return currentPretty.split('\n').map((line) => ({
        kind: 'same' as const,
        tokens: this.tokenizeLine(line)
      }));
    }
    const previousPretty = this.toPrettyJson(this.sortValue(previousValue));
    return this.diffLines(previousPretty.split('\n'), currentPretty.split('\n'));
  }

  private sortValue(value: unknown): unknown {
    if (Array.isArray(value)) {
      const items = value.map((item) => this.sortValue(item));
      if (items.every((item) => this.itemId(item) !== null)) {
        return [...items].sort((a, b) => String(this.itemId(a)).localeCompare(String(this.itemId(b)), undefined, { numeric: true }));
      }
      return items;
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      return Object.keys(record)
        .sort((a, b) => a.localeCompare(b))
        .reduce<Record<string, unknown>>((acc, key) => {
          acc[key] = this.sortValue(record[key]);
          return acc;
        }, {});
    }
    return value;
  }

  private diffLines(oldLines: string[], newLines: string[]): JsonDisplayLine[] {
    const n = oldLines.length;
    const m = newLines.length;
    const dp: number[][] = Array.from({ length: n + 1 }, () => Array(m + 1).fill(0));
    for (let i = n - 1; i >= 0; i--) {
      for (let j = m - 1; j >= 0; j--) {
        dp[i][j] = oldLines[i] === newLines[j]
          ? dp[i + 1][j + 1] + 1
          : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const result: JsonDisplayLine[] = [];
    let i = 0;
    let j = 0;
    while (i < n && j < m) {
      if (oldLines[i] === newLines[j]) {
        result.push({ kind: 'same', tokens: this.tokenizeLine(oldLines[i]) });
        i += 1;
        j += 1;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        result.push({ kind: 'del', tokens: this.tokenizeLine(oldLines[i]) });
        i += 1;
      } else {
        result.push({ kind: 'add', tokens: this.tokenizeLine(newLines[j]) });
        j += 1;
      }
    }
    while (i < n) {
      result.push({ kind: 'del', tokens: this.tokenizeLine(oldLines[i]) });
      i += 1;
    }
    while (j < m) {
      result.push({ kind: 'add', tokens: this.tokenizeLine(newLines[j]) });
      j += 1;
    }
    return result;
  }

  private toPrettyJson(value: unknown): string {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return '"[unserializable]"';
    }
  }

  private tokenizeLine(line: string): JsonToken[] {
    const tokens: JsonToken[] = [];
    const pattern =
      /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|\b(true|false)\b|\b(null)\b|([\{\}\[\]:,])|(\s+)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({ type: 'punctuation', text: line.slice(lastIndex, match.index) });
      }
      if (match[1] !== undefined) {
        if (match[2] !== undefined) {
          tokens.push({ type: 'key', text: match[1] });
          tokens.push({ type: 'punctuation', text: match[2] });
        } else {
          tokens.push({ type: 'string', text: match[1] });
        }
      } else if (match[3] !== undefined) {
        tokens.push({ type: 'number', text: match[3] });
      } else if (match[4] !== undefined) {
        tokens.push({ type: 'boolean', text: match[4] });
      } else if (match[5] !== undefined) {
        tokens.push({ type: 'null', text: match[5] });
      } else if (match[6] !== undefined) {
        tokens.push({ type: 'punctuation', text: match[6] });
      } else if (match[7] !== undefined) {
        tokens.push({ type: 'whitespace', text: match[7] });
      }
      lastIndex = pattern.lastIndex;
    }
    if (lastIndex < line.length) {
      tokens.push({ type: 'punctuation', text: line.slice(lastIndex) });
    }
    return tokens.length ? tokens : [{ type: 'whitespace', text: '' }];
  }
}
